import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import {
  WS_PATH,
  type AdsState,
  type ChatMessage,
  type ClientMessage,
  type ClipsState,
  type MyClipsState,
  type EventSummary,
  type PipLayout,
  type CameraLook,
  type OutputState,
  type Presence,
  type ResultsStatus,
  type RestreamStatus,
  type StageLook,
  type RTCIceServerLike,
  type SelfInfo,
  type ServerMessage,
  type StreamSource,
  type TickerEntry,
  type TickerState,
  type SpotlightState,
  type WeatherState,
  type WidgetState,
} from '@rabbitfuel/shared';
import {
  defaultOutput,
  defaultPip,
  defaultStage,
  defaultWidgets,
  emptyAds,
  emptyTicker,
  emptyMyClips,
  emptySpotlight,
  emptyWeather,
  idleRestream,
  idleResults,
} from '@rabbitfuel/shared';
import type { Session } from './session.ts';
import { readToken } from './auth.tsx';
import { clientId, deviceId } from './device.ts';

export type ConnStatus = 'connecting' | 'open' | 'closed';

export interface RealtimeState {
  status: ConnStatus;
  self: SelfInfo | null;
  event: EventSummary | null;
  sources: StreamSource[];
  activeSourceId: string | null;
  overlaySourceId: string | null;
  /** Ecke und Groesse des kleinen Fensters. */
  pip: PipLayout;
  chat: ChatMessage[];
  presence: Presence;
  ticker: TickerState;
  tickerFeed: TickerEntry[];
  widgets: WidgetState;
  /** Hintergrund hinter dem Kamerabild. */
  stage: StageLook;
  /** Bildkorrektur je Quelle, adressiert ueber die sourceId. */
  cameras: Record<string, CameraLook>;
  /** Zustand des RTMP-Ausgangs. Liegt auf dem Server, nicht in diesem Tab. */
  restream: RestreamStatus;
  results: ResultsStatus;
  /**
   * Ton und Bild am Ausgang -- der Master-Griff der Regie. Liegt auf dem
   * Server, damit Zuschauersicht und RTMP-Ausgang dasselbe zeigen und ein
   * Reload in der Regie nichts davon verliert.
   */
  output: OutputState;
  /** Sponsoren, Logo im Bild und eine laufende Werbepause. */
  ads: AdsState;
  /**
   * Wann `ads` hier ankam (`performance.now()`). Ab da zaehlt die Werbepause
   * mit der eigenen Uhr weiter -- siehe `AdBreak.elapsedMs`.
   */
  adsAt: number;
  /** Wetter-Einstellung der Regie und die letzte Vorhersage vom Server. */
  weather: WeatherState;
  /** Alle Botschaften -- nur in der Regie, sonst null. */
  clips: ClipsState | null;
  /** Die eigenen Botschaften und die Grenze dieses Absenders. */
  myClips: MyClipsState;
  /** Athleten-Pop-up: das laufende (oder keins) und Ecke/Groesse/Farbe. */
  spotlight: SpotlightState;
  iceServers: RTCIceServerLike[];
  instanceId: string | null;
  error: { code: string; message: string } | null;
}

const initialState: RealtimeState = {
  status: 'connecting',
  self: null,
  event: null,
  sources: [],
  activeSourceId: null,
  overlaySourceId: null,
  pip: defaultPip(),
  chat: [],
  presence: { total: 0, athletes: 0, spectators: 0, organizers: 0 },
  ticker: emptyTicker(),
  tickerFeed: [],
  widgets: defaultWidgets(),
  stage: defaultStage(),
  cameras: {},
  restream: idleRestream(),
  results: idleResults(),
  output: defaultOutput(),
  ads: emptyAds(),
  adsAt: 0,
  weather: emptyWeather(),
  clips: null,
  myClips: emptyMyClips(),
  spotlight: emptySpotlight(),
  iceServers: [],
  instanceId: null,
  error: null,
};

interface RealtimeApi {
  state: RealtimeState;
  send: (msg: ClientMessage) => void;
  /** Roher Nachrichtenstrom -- die WebRTC-Teile haengen sich hier ein. */
  on: (handler: (msg: ServerMessage) => void) => () => void;
  clearError: () => void;
}

const Ctx = createContext<RealtimeApi | null>(null);

function wsUrl() {
  const proto = location.protocol === 'https:' ? 'wss' : 'ws';
  return `${proto}://${location.host}${WS_PATH}`;
}

/** So oft fragt der Client nach, ob die Verbindung noch traegt. */
const PING_EVERY_MS = 5_000;
/**
 * Kommt nach einem Ping so lange gar nichts zurueck, ist die Leitung tot --
 * auch wenn der Browser die WebSocket noch fuer offen haelt. Genau das
 * passiert nach einem Netzwechsel am Handy: die alte TCP-Verbindung ist weg,
 * aber niemand hat es ihr gesagt, und ein `close()` darauf kann Minuten
 * brauchen. Ohne diese Pruefung merkt es nur der Server (Heartbeat) und
 * beendet die Quelle nach der Schonfrist, waehrend das Handy weiter glaubt,
 * es sei verbunden.
 */
const PONG_TIMEOUT_MS = 10_000;

export function RealtimeProvider({
  eventId,
  session,
  children,
}: {
  eventId: string;
  session: Session;
  children: ReactNode;
}) {
  const [state, setState] = useState<RealtimeState>(initialState);
  const socketRef = useRef<WebSocket | null>(null);
  const handlersRef = useRef(new Set<(msg: ServerMessage) => void>());
  const queueRef = useRef<ClientMessage[]>([]);
  const closedByUs = useRef(false);

  const send = useCallback((msg: ClientMessage) => {
    const socket = socketRef.current;
    if (socket && socket.readyState === WebSocket.OPEN) socket.send(JSON.stringify(msg));
    else queueRef.current.push(msg);
  }, []);

  const on = useCallback((handler: (msg: ServerMessage) => void) => {
    handlersRef.current.add(handler);
    return () => {
      handlersRef.current.delete(handler);
    };
  }, []);

  const clearError = useCallback(() => setState((s) => ({ ...s, error: null })), []);

  useEffect(() => {
    closedByUs.current = false;
    let attempt = 0;
    let retryTimer: number | undefined;
    let disposed = false;

    const connect = () => {
      if (disposed) return;
      setState((s) => ({ ...s, status: 'connecting' }));
      const socket = new WebSocket(wsUrl());
      socketRef.current = socket;
      let lastSeen = Date.now();
      /** Wann der offene Ping raus ist; 0 = keiner offen. */
      let pingedAt = 0;
      let gone = false;

      /* Genau einmal pro Socket: aufraeumen und -- wenn nicht gewollt -- neu verbinden. */
      const lost = () => {
        if (gone) return;
        gone = true;
        window.clearInterval(watchdog);
        if (socketRef.current === socket) socketRef.current = null;
        if (disposed || closedByUs.current) return;
        setState((s) => ({ ...s, status: 'closed' }));
        attempt += 1;
        const delay = Math.min(8000, 400 * 2 ** Math.min(attempt, 5));
        retryTimer = window.setTimeout(connect, delay);
      };

      /*
       * Gemessen wird ab dem Ping, nicht ab der letzten Nachricht: ein Tab im
       * Hintergrund bekommt seine Timer nur noch einmal pro Minute, und eine
       * ruhige Minute ohne Nachricht ist dort kein Abbruch.
       */
      const watchdog = window.setInterval(() => {
        if (socket.readyState !== WebSocket.OPEN) return;
        if (pingedAt && lastSeen < pingedAt) {
          if (Date.now() - pingedAt < PONG_TIMEOUT_MS) return;
          // Nicht auf das Schliessen warten -- bei einer toten Leitung kommt es spaet.
          socket.onclose = null;
          socket.onmessage = null;
          socket.onerror = null;
          socket.close();
          lost();
          return;
        }
        pingedAt = Date.now();
        socket.send(JSON.stringify({ t: 'ping' } satisfies ClientMessage));
      }, PING_EVERY_MS);

      socket.onopen = () => {
        attempt = 0;
        lastSeen = Date.now();
        socket.send(
          JSON.stringify({
            t: 'hello',
            eventId,
            role: session.role,
            displayName: session.displayName,
            bib: session.bib,
            // Konto-Token ist der regulaere Regie-Weg, die PIN der Notfall.
            authToken: readToken() ?? undefined,
            organizerPin: session.organizerPin,
            // Damit der Server eine laufende Quelle dieses Tabs nach einem
            // Reload wiedererkennt, statt sie als Abbruch zu behandeln.
            deviceId: deviceId(),
            clientId: clientId(),
          } satisfies ClientMessage),
        );
        for (const queued of queueRef.current.splice(0)) socket.send(JSON.stringify(queued));
        setState((s) => ({ ...s, status: 'open' }));
      };

      socket.onmessage = (ev) => {
        lastSeen = Date.now();
        let msg: ServerMessage;
        try {
          msg = JSON.parse(ev.data as string) as ServerMessage;
        } catch {
          return;
        }

        setState((prev) => {
          switch (msg.t) {
            case 'welcome':
              return {
                ...prev,
                status: 'open',
                self: msg.self,
                event: msg.event,
                sources: msg.sources,
                activeSourceId: msg.activeSourceId,
                overlaySourceId: msg.overlaySourceId,
                pip: msg.pip,
                chat: msg.chat,
                presence: msg.presence,
                ticker: msg.ticker,
                tickerFeed: msg.tickerFeed,
                widgets: msg.widgets,
                stage: msg.stage,
                cameras: msg.cameras,
                restream: msg.restream,
                results: msg.results,
                output: msg.output,
                ads: msg.ads,
                adsAt: performance.now(),
                weather: msg.weather,
                clips: msg.clips,
                myClips: msg.myClips,
                spotlight: msg.spotlight,
                iceServers: msg.iceServers,
                instanceId: msg.instanceId,
                error: null,
              };
            case 'sources':
              return { ...prev, sources: msg.sources };
            case 'program':
              return {
                ...prev,
                activeSourceId: msg.activeSourceId,
                overlaySourceId: msg.overlaySourceId,
                pip: msg.pip,
              };
            case 'chat':
              return { ...prev, chat: [...prev.chat, msg.message].slice(-200) };
            case 'presence':
              return { ...prev, presence: msg.presence };
            case 'ticker':
              return { ...prev, ticker: msg.ticker, tickerFeed: msg.feed };
            case 'widgets':
              return { ...prev, widgets: msg.widgets };
            case 'look':
              return { ...prev, stage: msg.stage, cameras: msg.cameras };
            case 'restream':
              return { ...prev, restream: msg.status };
            case 'results':
              return { ...prev, results: msg.status };
            case 'output':
              return { ...prev, output: msg.output };
            case 'ads':
              return { ...prev, ads: msg.ads, adsAt: performance.now() };
            case 'weather':
              return { ...prev, weather: msg.weather };
            case 'clips':
              return { ...prev, clips: msg.clips };
            case 'clips:mine':
              return { ...prev, myClips: msg.mine };
            case 'spotlight':
              return { ...prev, spotlight: msg.spotlight };
            case 'error':
              return { ...prev, error: { code: msg.code, message: msg.message } };
            default:
              return prev;
          }
        });

        for (const handler of handlersRef.current) handler(msg);
      };

      socket.onclose = lost;
      socket.onerror = () => socket.close();
    };

    connect();

    return () => {
      disposed = true;
      closedByUs.current = true;
      window.clearTimeout(retryTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [eventId, session.role, session.displayName, session.bib, session.organizerPin]);

  const api = useMemo<RealtimeApi>(() => ({ state, send, on, clearError }), [state, send, on, clearError]);
  return <Ctx.Provider value={api}>{children}</Ctx.Provider>;
}

export function useRealtime(): RealtimeApi {
  const ctx = useContext(Ctx);
  if (!ctx) throw new Error('useRealtime muss innerhalb von <RealtimeProvider> genutzt werden.');
  return ctx;
}
